import React from "react";
import Navbar from "../Navbar/Navbar";
import { Link } from "react-router-dom";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

const projects = [
  {
    title: "Devdesk Pool",
    category: "SaaS Platform",
    description:
      "Pool betting platform powered by Devdesk Pool. Runs across POS terminals, Android, iOS, and web, handling real-time odds, transactions, and user management at scale.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2426&auto=format&fit=crop",
    tags: [ "Web", "Android", "iOS", "POS"],
    link: "https://www.debuxtonfixedodds.com/",
    featured: true,
  },
 
  {
    title: "Auditra",
    category: "Upcoming: AI Accounting",
    description:
      "An AI-powered accounting management solution designed to take businesses from paper-based bookkeeping to a paperless, intelligent financial system. Features a developer-friendly API for integration into existing software.",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2940&auto=format&fit=crop",
    tags: ["AI", "Accounting", "API", "SaaS"],
    link: null,
    featured: false,
    upcoming: true,
  },
];

export default function HeroWork() {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-50 rounded-full blur-3xl opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-8 border border-blue-100">
              <div className="w-1.5 h-1.5 rounded-full bg-[#0186ef]" />
              <span className="text-xs font-semibold text-[#0186ef] uppercase tracking-wider">
                Our Work
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.1] tracking-tight mb-6">
              Products we've{" "}
              <span className="text-[#0186ef]">shipped</span>
            </h1>

            <p className="text-lg text-gray-500 leading-relaxed max-w-2xl">
              A look at the real-world projects we've built, from SaaS
              platforms powering daily transactions to web and mobile solutions
              driving business growth.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Project */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden">
                <img
                  src={projects[0].image}
                  alt={projects[0].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 left-6">
                  <span className="inline-flex px-3 py-1.5 bg-[#0186ef] text-white rounded-full text-xs font-semibold uppercase tracking-wider">
                    Featured
                  </span>
                </div>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="text-xs font-semibold text-[#0186ef] uppercase tracking-wider mb-3">
                  {projects[0].category}
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  {projects[0].title}
                </h2>
                <p className="text-gray-500 leading-relaxed mb-6">
                  {projects[0].description}
                </p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {projects[0].tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <a
                  href={projects[0].link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#0186ef] hover:underline self-start"
                >
                  Visit live site
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Projects */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">
            More projects
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.slice(1).map((project, i) => (
              <div
                key={i}
                className="group rounded-3xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="inline-flex px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-gray-700">
                      {project.category}
                    </span>
                    {project.upcoming && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/90 backdrop-blur-sm rounded-full text-xs font-semibold text-white">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        Coming Soon
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6 lg:p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#0186ef] transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex px-2.5 py-0.5 bg-gray-100 rounded-full text-xs font-medium text-gray-500"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-16 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Want to see your project here?
          </h2>
          <p className="text-lg text-gray-400 mb-10 max-w-xl mx-auto">
            Let's talk about what you're building and how we can bring it to
            life.
          </p>
          <Link
            to="/contact"
            className="inline-flex px-8 py-4 text-sm font-semibold text-gray-900 bg-white rounded-full hover:bg-gray-100 transition-all duration-300 hover:shadow-lg"
          >
            Start a Project
          </Link>
        </div>
      </section>
    </>
  );
}
